import { useMemo, useState, useEffect, useCallback } from 'react'
import { LogEntry } from '../types/shard'

// ACA Consumption tier pricing
const VCPU_PER_SECOND = 0.000012    // $0.000012 per vCPU-second
const GIB_PER_SECOND = 0.0000015    // $0.0000015 per GiB-second
const AGENT_VCPU = 1                // 1 vCPU per agent container
const AGENT_GIB = 2                 // 2 GiB per agent container

export interface ContainerRun {
  persona: string
  taskId: string
  startTime: Date
  endTime: Date | null
  durationSeconds: number
}

export interface PersonaInfraCost {
  persona: string
  totalSeconds: number
  cost: number
  runs: number
}

export interface InfraCostMetrics {
  totalInfraCost: number
  totalExecutionSeconds: number
  totalRuns: number
  byPersona: PersonaInfraCost[]
  containerRuns: ContainerRun[]
}

// Backend cost estimate response (from Temporal workflow history)
interface BackendCostEstimate {
  executions: {
    persona: string
    taskId: string
    durationSeconds: number
  }[]
  totalExecutionSeconds: number
}

/**
 * Parse activity logs for container start/end events to calculate infra cost.
 *
 * Looks for patterns like:
 *   "[Persona] START task TASK-XXX" and "[Persona] DONE task TASK-XXX"
 *   or container lifecycle events from the cloud dispatcher
 */
function parseContainerRuns(logs: LogEntry[]): ContainerRun[] {
  const runs: ContainerRun[] = []
  // Track open runs by taskId
  const openRuns = new Map<string, { persona: string; startTime: Date }>()

  for (const log of logs) {
    const msg = log.message

    // Extract persona from [Persona] prefix
    const personaMatch = msg.match(/^\[(\w+)\]/)
    if (!personaMatch) continue
    const persona = personaMatch[1]!.toLowerCase()

    // Match START events
    const startMatch = msg.match(/START\s+task\s+((?:TASK|SPEC)-\d+)/i)
    if (startMatch) {
      const taskId = startMatch[1]!
      openRuns.set(taskId, {
        persona,
        startTime: new Date(log.timestamp),
      })
      continue
    }

    // Match DONE events
    const doneMatch = msg.match(/DONE\s+task\s+((?:TASK|SPEC)-\d+)/i)
    if (doneMatch) {
      const taskId = doneMatch[1]!
      const open = openRuns.get(taskId)
      if (open) {
        const endTime = new Date(log.timestamp)
        const durationSeconds = Math.max(0, (endTime.getTime() - open.startTime.getTime()) / 1000)
        runs.push({
          persona: open.persona,
          taskId,
          startTime: open.startTime,
          endTime,
          durationSeconds,
        })
        openRuns.delete(taskId)
      }
      continue
    }
  }

  // Add still-running containers (open runs)
  const now = new Date()
  for (const [taskId, open] of openRuns) {
    const durationSeconds = Math.max(0, (now.getTime() - open.startTime.getTime()) / 1000)
    runs.push({
      persona: open.persona,
      taskId,
      startTime: open.startTime,
      endTime: null,
      durationSeconds,
    })
  }

  return runs
}

function calculateInfraCost(durationSeconds: number): number {
  const vcpuCost = durationSeconds * AGENT_VCPU * VCPU_PER_SECOND
  const memoryCost = durationSeconds * AGENT_GIB * GIB_PER_SECOND
  return vcpuCost + memoryCost
}

function aggregateByPersona(runs: ContainerRun[]): PersonaInfraCost[] {
  const agg = new Map<string, { totalSeconds: number; cost: number; runs: number }>()

  for (const run of runs) {
    const existing = agg.get(run.persona) || { totalSeconds: 0, cost: 0, runs: 0 }
    existing.totalSeconds += run.durationSeconds
    existing.cost += calculateInfraCost(run.durationSeconds)
    existing.runs += 1
    agg.set(run.persona, existing)
  }

  return Array.from(agg.entries())
    .map(([persona, data]) => ({ persona, ...data }))
    .sort((a, b) => b.cost - a.cost)
}

export function useInfraCost(logs: LogEntry[]): InfraCostMetrics {
  const [backendData, setBackendData] = useState<BackendCostEstimate | null>(null)

  // Fetch backend cost estimate on mount and every 30s
  const fetchCostEstimate = useCallback(async () => {
    try {
      const resp = await fetch('/api/cost/estimate')
      if (resp.ok) {
        const data: BackendCostEstimate = await resp.json()
        setBackendData(data)
      }
    } catch {
      // Backend may not be available (local mode) - silently ignore
    }
  }, [])

  useEffect(() => {
    fetchCostEstimate()
    const interval = setInterval(fetchCostEstimate, 30000)
    return () => clearInterval(interval)
  }, [fetchCostEstimate])

  return useMemo(() => {
    // Parse container runs from activity logs
    const logRuns = parseContainerRuns(logs)

    // Merge with backend data if available
    let allRuns = logRuns
    if (backendData) {
      // Backend data provides more accurate durations from Temporal history
      // Merge: use backend data for completed runs, log data for active runs
      const backendRuns: ContainerRun[] = backendData.executions.map(e => ({
        persona: e.persona,
        taskId: e.taskId,
        startTime: new Date(), // Not tracked in backend summary
        endTime: new Date(),
        durationSeconds: e.durationSeconds,
      }))

      // Use backend runs for completed tasks, keep log-based active runs
      const completedTaskIds = new Set(backendRuns.map(r => r.taskId))
      const activeLogRuns = logRuns.filter(r => r.endTime === null && !completedTaskIds.has(r.taskId))
      allRuns = [...backendRuns, ...activeLogRuns]
    }

    const byPersona = aggregateByPersona(allRuns)
    const totalExecutionSeconds = allRuns.reduce((sum, r) => sum + r.durationSeconds, 0)
    const totalInfraCost = calculateInfraCost(totalExecutionSeconds)

    return {
      totalInfraCost,
      totalExecutionSeconds,
      totalRuns: allRuns.length,
      byPersona,
      containerRuns: allRuns,
    }
  }, [logs, backendData])
}
